import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { channelWa } from '@repletoryreach/sdk-api';
import { Stack, Group, Button, Text, Title, List } from '@repletoryreach/sdk-ui';
import { Alert, Stepper } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { TextInput, Checkbox, SegmentedControl, PhoneInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../i18n/I18nProvider';
import { phoneLabels } from '../../lib/phone';
import { MetaRefusalNotice, refusalFrom, type Refusal } from './MetaRefusalNotice';

/*
 * The guided move of a number out of the WhatsApp Business app.
 *
 * A number signed in to the app on a phone cannot be used here, and nothing
 * on the platform can free it: only the person holding the phone can delete
 * the account in the app. So this walks them through the one thing only they
 * can do, and the platform does everything after it: notices when the number
 * is free, sends the code, takes the six digits, registers the number and
 * connects it.
 *
 * Six steps, written for a shop owner. Nothing proceeds past the first until
 * they have chosen, because the choice is real: the app stops working on that
 * number, and the chats on the phone do not move across. Closing this window
 * midway changes nothing; opening it again with the same number carries on
 * from where WhatsApp says the number is.
 */

const STEPS = ['why', 'save', 'delete', 'check', 'verify', 'connect'] as const;
type StepKey = (typeof STEPS)[number];

type Session = channelWa.MoveFromAppSession;

export function MoveFromApp({
  opened,
  onClose,
  initialMsisdn,
  initialPhoneNumberId,
  onConnected
}: {
  opened: boolean;
  onClose: () => void;
  initialMsisdn?: string;
  initialPhoneNumberId?: string;
  /** Called once the number is attached, so the page can refresh its list. */
  onConnected?: () => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const [step, setStep] = useState<StepKey>('why');
  const [session, setSession] = useState<Session | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [busy, setBusy] = useState(false);
  const [msisdn, setMsisdn] = useState(initialMsisdn ?? '');
  const [msisdnBad, setMsisdnBad] = useState(false);
  const [msisdnError, setMsisdnError] = useState('');
  const [saved, setSaved] = useState(false);
  const [keepApp, setKeepApp] = useState(false);
  const [method, setMethod] = useState<'sms' | 'voice'>('sms');
  const [codeSent, setCodeSent] = useState<'sms' | 'voice' | null>(null);
  const [code, setCode] = useState('');
  const [stopped, setStopped] = useState(false);
  const [checkDetail, setCheckDetail] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // A fresh window each time it opens, with the number the refusal named.
  const lastOpened = useRef(false);
  useEffect(() => {
    if (opened && !lastOpened.current) {
      setStep('why'); setSession(null); setRefusal(null); setBusy(false);
      setMsisdn(initialMsisdn ?? ''); setMsisdnError(''); setSaved(false); setKeepApp(false);
      setMethod('sms'); setCodeSent(null); setCode(''); setStopped(false); setCheckDetail(null); setDone(false);
    }
    lastOpened.current = opened;
  }, [opened, initialMsisdn]);

  const number = session?.msisdn ?? session?.number?.msisdn ?? msisdn;

  /** Which step the platform says to show, turned into ours. */
  function follow(s: Session) {
    setSession(s);
    switch (s.next) {
      case 'delete_app': setStep((cur) => (cur === 'why' ? 'save' : cur === 'check' ? 'check' : cur)); break;
      case 'verify': setStep('verify'); break;
      case 'connect': setStep('connect'); break;
      case 'done': setStep('connect'); setDone(true); break;
      default: break;
    }
  }

  async function run<T>(fn: () => Promise<T>): Promise<T | null> {
    setBusy(true);
    setRefusal(null);
    try {
      return await fn();
    } catch (e) {
      const r = refusalFrom(e);
      if (r) setRefusal(r);
      else setRefusal({ code: 'META_REFUSED', message: e instanceof Error ? e.message : t('common.unknownError'), action: 'retry' });
      return null;
    } finally {
      setBusy(false);
    }
  }

  // Step 1 → the platform checks the number with WhatsApp BEFORE anybody
  // deletes anything on the phone: the plan's allowance, a number held by
  // another provider and a full account all come back here, while the app is
  // still intact.
  async function start() {
    const id = initialPhoneNumberId?.trim();
    // The field hands back the canonical form, or nothing while the number
    // does not read; it has already said why under itself.
    if (!id && !msisdn) {
      setMsisdnError(msisdnBad ? t('phone.fixNumber') : t('numbers.move.why.numberHint'));
      return;
    }
    const res = await run(() => channelWa.startMoveFromApp(id ? { phone_number_id: id, msisdn: msisdn || undefined } : { msisdn }));
    if (!res) return;
    const s = res.data;
    setSession(s);
    if (s.next === 'delete_app') setStep('save');
    else follow(s);
  }

  async function check() {
    if (!session) return;
    setStep('check');
    setCheckDetail(null);
    const res = await run(() => channelWa.getMoveFromApp(session.id));
    if (!res) return;
    const s = res.data;
    setSession(s);
    if (s.next === 'delete_app' || s.next === 'check') {
      setCheckDetail(s.check_detail ?? (s.next === 'check' ? t('numbers.move.check.noAnswer') : t('numbers.move.check.stillOnApp')));
      return;
    }
    follow(s);
  }

  async function sendCode() {
    if (!session) return;
    const res = await run(() => channelWa.requestMoveCode(session.id, { method }));
    if (!res) return;
    setSession(res.data);
    setCodeSent(method);
    setCode('');
  }

  async function verify() {
    if (!session) return;
    const digits = code.replace(/\D/g, '');
    if (digits.length !== 6) { setRefusal({ code: 'CODE_MISMATCH', message: t('numbers.move.verify.codeHint'), action: 'retry_code' }); return; }
    const res = await run(() => channelWa.verifyMoveCode(session.id, digits));
    if (!res) return;
    setSession(res.data);
    setStep('connect');
  }

  async function connect() {
    if (!session) return;
    const res = await run(() => channelWa.registerMoveFromApp(session.id));
    if (!res) return;
    setSession(res.data);
    setDone(true);
    notifySuccess(t('numbers.connect.done', { number: res.data.msisdn ?? number }), t('numbers.connect.doneTitle'));
    onConnected?.();
  }

  async function stop() {
    if (session && !done) {
      await channelWa.cancelMoveFromApp(session.id).catch(() => {});
    }
    setStopped(true);
  }

  const active = STEPS.indexOf(step);

  return (
    <Modal opened={opened} onClose={onClose} title={t('numbers.move.title')} size="lg" closeOnClickOutside={false}>
      <div className="rr-move">
        <div className="rr-move__steps">
          <Stepper active={done ? STEPS.length : active} orientation="horizontal" size="xs">
            {STEPS.map((s) => <Stepper.Step key={s} label={t(`numbers.move.step.${s}`)} />)}
          </Stepper>
        </div>

        {stopped ? (
          <Stack gap="md">
            <Alert color="info">{t('numbers.move.stopped')}</Alert>
            <Group justify="flex-end"><Button onClick={onClose}>{t('numbers.move.done.close')}</Button></Group>
          </Stack>
        ) : (
          <Stack gap="md" className="rr-move__body">
            {refusal && <MetaRefusalNotice refusal={refusal} inMove onClose={() => setRefusal(null)} />}

            {/* ── 1. Why ──────────────────────────────────────────────── */}
            {step === 'why' && (
              <Stack gap="md">
                <Text role="body">{t('numbers.move.why.rule')}</Text>
                <div className="rr-move__columns">
                  <div>
                    <Title order={5}>{t('numbers.move.why.gainTitle')}</Title>
                    <List size="sm" spacing="xs">
                      <List.Item>{t('numbers.move.why.gain1')}</List.Item>
                      <List.Item>{t('numbers.move.why.gain2')}</List.Item>
                      <List.Item>{t('numbers.move.why.gain3')}</List.Item>
                      <List.Item>{t('numbers.move.why.gain4')}</List.Item>
                    </List>
                  </div>
                  <div>
                    <Title order={5}>{t('numbers.move.why.changeTitle')}</Title>
                    <List size="sm" spacing="xs">
                      <List.Item>{t('numbers.move.why.change1')}</List.Item>
                      <List.Item>{t('numbers.move.why.change2')}</List.Item>
                      <List.Item>{t('numbers.move.why.change3')}</List.Item>
                    </List>
                  </div>
                </div>

                {!initialPhoneNumberId && (
                  <PhoneInput
                    label={t('numbers.move.why.number')}
                    description={t('numbers.move.why.numberHint')}
                    value={msisdn}
                    error={msisdnError}
                    onChange={(e164, meta) => { setMsisdn(e164); setMsisdnBad(!meta.valid && meta.raw.trim() !== ''); setMsisdnError(''); }}
                    labels={phoneLabels(t)}
                    required
                  />
                )}
                {initialPhoneNumberId && number && (
                  <Text role="bodySmall"><strong>{t('numbers.move.why.number')}:</strong> {number}</Text>
                )}

                {keepApp ? (
                  <Alert color="info">{t('numbers.move.why.keepAppNote')}</Alert>
                ) : (
                  <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('numbers.move.why.alternative')}</Text>
                )}

                <Group justify="space-between" wrap="wrap" className="rr-move__footer">
                  <Button variant="default" onClick={() => { if (keepApp) onClose(); else setKeepApp(true); }}>
                    {keepApp ? t('numbers.move.done.close') : t('numbers.move.why.keepApp')}
                  </Button>
                  <Button loading={busy} onClick={start} disabled={keepApp}>{t('numbers.move.why.confirm')}</Button>
                </Group>
                {busy && <Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.move.starting')}</Text>}
              </Stack>
            )}

            {/* ── 2. Save your chats ──────────────────────────────────── */}
            {step === 'save' && (
              <Stack gap="md">
                <Text role="body">{t('numbers.move.save.intro')}</Text>
                <div className="rr-move__columns">
                  <div>
                    <Title order={5}>{t('numbers.move.save.backupTitle')}</Title>
                    <List type="ordered" size="sm" spacing="xs">
                      <List.Item>{t('numbers.move.save.backup1')}</List.Item>
                      <List.Item>{t('numbers.move.save.backup2')}</List.Item>
                      <List.Item>{t('numbers.move.save.backup3')}</List.Item>
                    </List>
                  </div>
                  <div>
                    <Title order={5}>{t('numbers.move.save.exportTitle')}</Title>
                    <List type="ordered" size="sm" spacing="xs">
                      <List.Item>{t('numbers.move.save.export1')}</List.Item>
                      <List.Item>{t('numbers.move.save.export2')}</List.Item>
                    </List>
                  </div>
                </div>
                <Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.move.save.note')}</Text>
                <Checkbox
                  label={t('numbers.move.save.confirm')}
                  description={t('numbers.move.save.confirmHint')}
                  checked={saved}
                  onChange={setSaved}
                />
                <Footer
                  back={() => setStep('why')}
                  next={() => setStep('delete')}
                  nextLabel={t('numbers.move.next')}
                  nextDisabled={!saved}
                  stop={stop}
                />
              </Stack>
            )}

            {/* ── 3. Delete the account on the phone ──────────────────── */}
            {step === 'delete' && (
              <Stack gap="md">
                <Text role="body">{t('numbers.move.delete.intro')}</Text>
                <List type="ordered" size="sm" spacing="xs">
                  <List.Item>{t('numbers.move.delete.tap1')}</List.Item>
                  <List.Item>{t('numbers.move.delete.tap2')}</List.Item>
                  <List.Item>{t('numbers.move.delete.tap3')}</List.Item>
                  <List.Item>{t('numbers.move.delete.tap4', { number })}</List.Item>
                  <List.Item>{t('numbers.move.delete.tap5')}</List.Item>
                </List>
                <Alert color="info">{t('numbers.move.delete.note')}</Alert>
                <Footer
                  back={() => setStep('save')}
                  next={check}
                  nextLabel={t('numbers.move.delete.confirm')}
                  busy={busy}
                  stop={stop}
                />
              </Stack>
            )}

            {/* ── 4. Check ────────────────────────────────────────────── */}
            {step === 'check' && (
              <Stack gap="md">
                {busy ? (
                  <Text role="body">{t('numbers.move.check.checking')}</Text>
                ) : checkDetail ? (
                  <Alert color="warning">{checkDetail}</Alert>
                ) : (
                  <Alert color="success">{t('numbers.move.check.free')}</Alert>
                )}
                <Footer
                  back={() => setStep('delete')}
                  next={check}
                  nextLabel={t('numbers.move.check.again')}
                  busy={busy}
                  stop={stop}
                />
              </Stack>
            )}

            {/* ── 5. Verify ───────────────────────────────────────────── */}
            {step === 'verify' && (
              <Stack gap="md">
                <Text role="body">{t('numbers.move.verify.intro', { number })}</Text>
                <div>
                  <Text role="bodySmall" style={{ marginBottom: 6 }}>{t('numbers.move.verify.method')}</Text>
                  <SegmentedControl
                    size="sm"
                    value={method}
                    onChange={(v) => setMethod(v === 'voice' ? 'voice' : 'sms')}
                    data={[
                      { label: t('numbers.move.verify.sms'), value: 'sms' },
                      { label: t('numbers.move.verify.voice'), value: 'voice' }
                    ]}
                  />
                </div>
                <Group gap="xs" wrap="wrap">
                  <Button variant={codeSent ? 'default' : 'filled'} loading={busy && !codeSent} onClick={sendCode}>
                    {codeSent ? t('numbers.move.verify.resend') : t('numbers.move.verify.send')}
                  </Button>
                  {codeSent && (
                    <Text role="bodySmall" c="var(--rr-color-text-muted)">
                      {codeSent === 'sms'
                        ? t('numbers.move.verify.sent.sms', { number })
                        : t('numbers.move.verify.sent.voice', { number })}
                    </Text>
                  )}
                </Group>
                {codeSent && (
                  <form onSubmit={(e) => { e.preventDefault(); void verify(); }}>
                    <Stack gap="sm">
                      <TextInput
                        label={t('numbers.move.verify.code')}
                        description={t('numbers.move.verify.codeHint')}
                        value={code}
                        onChange={(e) => setCode(e.currentTarget.value.replace(/[^\d\s]/g, '').slice(0, 7))}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={7}
                        className="rr-move__code"
                        required
                      />
                      <Group justify="flex-end">
                        <Button type="submit" loading={busy}>{t('numbers.move.verify.confirm')}</Button>
                      </Group>
                    </Stack>
                  </form>
                )}
                <Footer back={() => setStep('check')} stop={stop} />
              </Stack>
            )}

            {/* ── 6. Connect, and the finish ──────────────────────────── */}
            {step === 'connect' && !done && (
              <Stack gap="md">
                {session?.number?.verified || session?.current_step === 'number_verified' ? (
                  <Alert color="success">{t('numbers.move.verify.done')}</Alert>
                ) : null}
                <Text role="body">{t('numbers.move.connect.intro')}</Text>
                {busy && <Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.move.connect.working')}</Text>}
                <Footer
                  next={connect}
                  nextLabel={t('numbers.move.connect.confirm')}
                  busy={busy}
                  stop={stop}
                />
              </Stack>
            )}
            {step === 'connect' && done && (
              <Stack gap="md">
                <Alert color="success" title={t('numbers.move.done.title', { number })}>
                  {t('numbers.move.done.test', { number })}
                </Alert>
                <Group justify="flex-end" gap="xs" wrap="wrap">
                  <Button variant="default" onClick={onClose}>{t('numbers.move.done.close')}</Button>
                  <Button onClick={() => { onClose(); navigate('/inbox'); }}>{t('numbers.move.done.inbox')}</Button>
                </Group>
              </Stack>
            )}
          </Stack>
        )}
      </div>
    </Modal>
  );
}

/** The footer every step after the first shares: back, stop, and the one action. */
function Footer({
  back,
  next,
  nextLabel,
  nextDisabled,
  busy,
  stop
}: {
  back?: () => void;
  next?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  busy?: boolean;
  stop: () => void;
}) {
  const t = useT();
  return (
    <Group justify="space-between" wrap="wrap" className="rr-move__footer">
      <Group gap="xs" wrap="wrap">
        {back && <Button variant="default" onClick={back} disabled={busy}>{t('numbers.move.back')}</Button>}
        <Button variant="subtle" onClick={stop} disabled={busy}>{t('numbers.move.stop')}</Button>
      </Group>
      {next && (
        <Button loading={busy} disabled={nextDisabled} onClick={next}>{nextLabel ?? t('numbers.move.next')}</Button>
      )}
    </Group>
  );
}
